/**
 * ============================================================================
 * [P2-01] Firestore Texts Service
 *
 * 글(texts) 컬렉션에 대한 CRUD 및 실시간 구독 함수
 *
 * 기능:
 * - createText: 글 생성
 * - updateText: 글 수정
 * - deleteText: 글 삭제
 * - getTexts: 글 목록 조회 (페이지네이션)
 * - subscribeToTexts: 실시간 구독
 *
 * Safety:
 * - withRetry()로 네트워크 에러 대응
 * - 모든 함수에서 userId/textId 검증
 * - onSnapshot unsubscribe 반환으로 메모리 누수 방지
 * ============================================================================
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  type DocumentSnapshot,
  type Query,
  type CollectionReference,
} from 'firebase/firestore'
import { db } from '../firebase'
import { withRetry } from '@/utils/common'
import { textToFirestore, textFromFirestore, textUpdatesToFirestore } from './converters'
import type { SavedText, FilterState } from '@/types'

// ============================================================================
// 컬렉션 참조 헬퍼
// ============================================================================
const getTextsCollection = (userId: string): CollectionReference =>
  collection(db, 'users', userId, 'texts')

// ============================================================================
// [P2-01-C] 글 생성 (Create)
// ============================================================================
export async function createText(
  userId: string,
  textData: Omit<SavedText, 'id' | 'createdAt' | 'updatedAt'>
): Promise<SavedText> {
  // 1. Validation
  if (!userId) {
    throw new Error('[createText] userId is required')
  }
  if (!textData.content?.trim()) {
    throw new Error('[createText] content is required')
  }

  // 2. Firestore에 추가 (재시도 로직 포함)
  return withRetry(async () => {
    const textsRef = getTextsCollection(userId)
    const docData = textToFirestore(textData)

    // 문서 추가
    const docRef = await addDoc(textsRef, docData)

    // 생성된 문서 조회
    const newDoc = await getDoc(docRef)
    if (!newDoc.exists()) {
      throw new Error('[createText] Failed to create document')
    }

    console.log('[createText] 글 생성 완료:', docRef.id)
    return textFromFirestore(newDoc)
  }, 3, 1000) // maxRetries=3, baseDelay=1000ms
}

// ============================================================================
// [P2-01-D] 글 수정 (Update)
// ============================================================================
export async function updateText(
  userId: string,
  textId: string,
  updates: Partial<SavedText>
): Promise<void> {
  // 1. Validation
  if (!userId) {
    throw new Error('[updateText] userId is required')
  }
  if (!textId) {
    throw new Error('[updateText] textId is required')
  }

  // 빈 업데이트는 무시
  const updateKeys = Object.keys(updates).filter(
    (key) => key !== 'id' && key !== 'createdAt'
  )
  if (updateKeys.length === 0) {
    console.warn('[updateText] No valid updates provided, skipping')
    return
  }

  // 2. Firestore 업데이트 (재시도 로직 포함)
  return withRetry(async () => {
    const textRef = doc(db, 'users', userId, 'texts', textId)
    const safeUpdates = textUpdatesToFirestore(updates)

    await updateDoc(textRef, safeUpdates)
    console.log('[updateText] 글 수정 완료:', textId)
  }, 3, 1000)
}

// ============================================================================
// [P2-01-E] 글 삭제 (Delete)
// ============================================================================
export async function deleteText(
  userId: string,
  textId: string
): Promise<void> {
  // 1. Validation
  if (!userId) {
    throw new Error('[deleteText] userId is required')
  }
  if (!textId) {
    throw new Error('[deleteText] textId is required')
  }

  // 2. Firestore 삭제 (재시도 로직 포함)
  return withRetry(async () => {
    const textRef = doc(db, 'users', userId, 'texts', textId)
    await deleteDoc(textRef)
    console.log('[deleteText] 글 삭제 완료:', textId)
  }, 3, 1000)
}

// ============================================================================
// [P2-01-F] 글 목록 조회 (페이지네이션)
// ============================================================================
export interface GetTextsResult {
  texts: SavedText[]
  lastDoc: DocumentSnapshot | null
  hasMore: boolean
}

export async function getTexts(
  userId: string,
  filter: FilterState,
  pageSize: number = 20,
  lastDoc: DocumentSnapshot | null = null
): Promise<GetTextsResult> {
  // 1. Validation
  if (!userId) {
    throw new Error('[getTexts] userId is required')
  }

  // 2. Firestore 조회 (재시도 로직 포함)
  return withRetry(async () => {
    const textsRef = getTextsCollection(userId)

    // 쿼리 빌드
    let q: Query = query(textsRef)

    // 정렬 적용
    switch (filter.sortBy) {
      case 'newest':
        q = query(q, orderBy('createdAt', 'desc'))
        break
      case 'oldest':
        q = query(q, orderBy('createdAt', 'asc'))
        break
      case 'charCount':
        q = query(q, orderBy('charCount', 'desc'))
        break
      default:
        q = query(q, orderBy('createdAt', 'desc'))
    }

    // 페이지네이션 적용 (+1로 hasMore 판단)
    q = query(q, limit(pageSize + 1))
    if (lastDoc) {
      q = query(q, startAfter(lastDoc))
    }

    // 쿼리 실행
    const snapshot = await getDocs(q)
    const docs = snapshot.docs
    const hasMore = docs.length > pageSize

    // 문서 변환
    const texts = docs
      .slice(0, pageSize)
      .map((docSnap) => textFromFirestore(docSnap))

    // 클라이언트 사이드 필터링 (검색)
    const filteredTexts = filter.search
      ? texts.filter((t) => {
          const searchLower = filter.search.toLowerCase()
          return (
            t.content.toLowerCase().includes(searchLower) ||
            t.title?.toLowerCase().includes(searchLower) ||
            t.topic?.toLowerCase().includes(searchLower)
          )
        })
      : texts

    console.log('[getTexts] 조회 완료:', filteredTexts.length, '개')

    return {
      texts: filteredTexts,
      lastDoc: docs[pageSize - 1] ?? null,
      hasMore,
    }
  }, 3, 1000)
}

// ============================================================================
// [P2-01-G] 실시간 구독 (Subscribe)
// - CRITICAL: 반환된 unsubscribe 함수를 반드시 cleanup에서 호출할 것
// ============================================================================
export function subscribeToTexts(
  userId: string,
  onData: (texts: SavedText[]) => void,
  onError?: (error: Error) => void
): () => void {
  // 1. Validation - userId가 없으면 no-op 반환
  if (!userId) {
    console.warn('[subscribeToTexts] userId is null, returning no-op unsubscribe')
    return () => {
      // no-op
    }
  }

  // 2. 쿼리 빌드
  const textsRef = getTextsCollection(userId)
  const q = query(textsRef, orderBy('createdAt', 'desc'))

  // 3. 구독 시작
  console.log('[subscribeToTexts] 실시간 구독 시작:', userId)

  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      const texts = snapshot.docs.map((docSnap) => textFromFirestore(docSnap))
      console.log('[subscribeToTexts] 데이터 수신:', texts.length, '개')
      onData(texts)
    },
    (error) => {
      console.error('[subscribeToTexts] 구독 에러:', error)
      onError?.(error)
    }
  )

  // 4. CRITICAL: unsubscribe 함수 반환 (메모리 누수 방지)
  return () => {
    console.log('[subscribeToTexts] 구독 해제:', userId)
    unsubscribe()
  }
}
